/*
 * <quantity-breaks>: bundle tiers ("1 Mask", "2 Masks" …). Each tier is a radio named "quantity"
 * bound to the product form plus, optionally, one variant <select> per item. The first select of
 * the checked tier mirrors the main variant picker in both directions. getItems() returns the
 * [{id, quantity}] list the product form posts; when every item is the same variant the form
 * falls back to a single-line add with the tier quantity.
 */
class QuantityBreaks extends HTMLElement {
  connectedCallback() {
    this.map = this.readMap();
    this.addEventListener('change', this.onChange.bind(this));
    this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
    this.syncFromMain();
  }

  disconnectedCallback() {
    this.unsubscribe?.();
  }

  readMap() {
    try {
      return JSON.parse(this.querySelector('[data-variant-map]')?.textContent || '[]');
    } catch (e) {
      return [];
    }
  }

  get productInfo() {
    return this.closest('product-info');
  }

  get variantSelects() {
    return this.productInfo?.querySelector('variant-selects');
  }

  get checkedTier() {
    const input = this.querySelector('input[name="quantity"]:checked');
    return input ? this.querySelector(`label[data-input="${input.dataset.input}"]`) : null;
  }

  selectorsFor(tier) {
    return tier ? Array.from(tier.querySelectorAll('.quantity-break__selector-item')) : [];
  }

  valuesFor(selectorItem) {
    return Array.from(selectorItem.querySelectorAll('select')).map((select) => select.value);
  }

  findVariant(values) {
    return this.map.find((variant) => variant.options.length === values.length && variant.options.every((v, i) => v === values[i]));
  }

  resolve(selectorItem) {
    const variant = this.findVariant(this.valuesFor(selectorItem));
    selectorItem.dataset.selectedId = variant ? variant.id : '';
    return variant;
  }

  onChange(event) {
    const target = event.target;

    if (target.matches('input[name="quantity"]')) {
      this.syncFromMain();
      return;
    }

    if (target.matches('select')) {
      const selectorItem = target.closest('.quantity-break__selector-item');
      if (!selectorItem) return;
      this.resolve(selectorItem);

      const isFirst = selectorItem.dataset.selectIndex === '0';
      const tier = selectorItem.closest('label.quantity-break');
      if (isFirst && tier === this.checkedTier && this.variantSelects && !this.syncing) {
        this.syncing = true;
        this.variantSelects.selectByValues?.(this.valuesFor(selectorItem));
        this.syncing = false;
      }
    }
  }

  /* Keep the first select of every tier in step with the main variant picker. */
  syncFromMain() {
    const values = this.variantSelects?.selectedValues;
    if (!values || !values.length) return;
    this.querySelectorAll('.quantity-break__selector-item[data-select-index="0"]').forEach((item) => {
      item.querySelectorAll('select').forEach((select, index) => {
        if (values[index] !== undefined && select.value !== values[index]) select.value = values[index];
      });
      this.resolve(item);
    });
  }

  onVariantChange({ data }) {
    if (!data?.variant) return;
    const values = data.variant.options || [];
    this.querySelectorAll('.quantity-break__selector-item[data-select-index="0"]').forEach((item) => {
      item.querySelectorAll('select').forEach((select, index) => {
        if (values[index] !== undefined) select.value = values[index];
      });
      this.resolve(item);
    });
  }

  getItems() {
    const tier = this.checkedTier;
    if (!tier) return null;
    const quantity = parseInt(tier.dataset.quantity) || 1;
    const selectors = this.selectorsFor(tier).slice(0, quantity);
    const fallbackId = this.productInfo?.querySelector('input[name="id"]')?.value;

    if (!selectors.length) {
      return fallbackId ? [{ id: parseInt(fallbackId), quantity }] : null;
    }

    const counts = new Map();
    selectors.forEach((item) => {
      const id = parseInt(item.dataset.selectedId || this.resolve(item)?.id || fallbackId);
      if (!id) return;
      counts.set(id, (counts.get(id) || 0) + 1);
    });
    return Array.from(counts, ([id, qty]) => ({ id, quantity: qty }));
  }
}

if (!customElements.get('quantity-breaks')) customElements.define('quantity-breaks', QuantityBreaks);
